import {
    Table,
    TableBody,
    TableCell,
    tableCellClasses,
    TableContainer,
    TableFooter,
    TableHead,
    TableRow,
    tableRowClasses,
} from '@mui/material';
import Column from '../../../../components/Column';
import { TableCellText, TablePrimaryCell } from '../../../../components/Metrics/MetricTable';
import { absChangeRatio, changeCode, formatNumber, formatPercent, ratio, subtract } from '../../HatUtils';
import { attributionExplainTooltip } from '../../HatDashboardPage';
import { HatChangeMetricWithSkeleton } from './HatChangeMetricWithSkeleton';
import { useHatDbContext } from '../../context/HatDbContext';
import {
    hatActionsFriendlyNames,
    HatMetricActionAttributionCount,
    HatMetricActionAttributionData,
} from '../dashboard/HatDbSectionActionAttribution';

// Width of skeleton placeholders in the table.
const skeletonWidth = 40;

// Actions are not really comparable as they are independant events, so sorting them by counts is not particularly
// useful. Instead, lets just define a fixed but arbitrary order based on what we assume is the most relevant data
// first.
export const displayOrder: (keyof HatMetricActionAttributionData)[] = [
    // Kinds of page views.
    'pageViewSrp',
    'pageViewVdp',
    'pageViewService',
    'pageViewSpecial',
    // Getting in touch.
    'pageViewContact',
    'clicksPhone',
    'copiedPhone',
    // Other actions.
    'formSubmit',
];

export function HatDbVisualActionAttributionTable(props: {
    currentData: HatMetricActionAttributionData;
    previousData: HatMetricActionAttributionData;
}) {
    const { currentData, previousData } = props;

    const hatDbContext = useHatDbContext();
    const hasPreviousInterval = !!hatDbContext.getPreviousRangeCriteria();

    const actionData: {
        label: string;
        current: HatMetricActionAttributionCount;
        previous: HatMetricActionAttributionCount;
    }[] = [];
    for (const key of displayOrder) {
        actionData.push({
            label: hatActionsFriendlyNames[key],
            current: currentData[key],
            previous: previousData[key],
        });
    }

    return (
        <TableContainer
            sx={{
                maxWidth: '100%',
                borderCollapse: 'inherit',
                borderColor: (theme) => theme.palette.grey[300],
                borderStyle: 'solid',
                borderWidth: 1,
                borderRadius: '8px',
                overflow: 'hidden',
            }}
        >
            <Table
                sx={{
                    [`& .${tableRowClasses.root} .${tableCellClasses.root}`]: {
                        width: '25%',
                        textAlign: 'right',
                    },

                    [`& .${tableRowClasses.root} .${tableCellClasses.root}:nth-of-type(1)`]: {
                        textAlign: 'left',
                    },
                }}
            >
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ minWidth: '130px' }}>Action</TableCell>
                        <TableCell>Total Actions</TableCell>
                        <TableCell>
                            <TableCellText tooltip={attributionExplainTooltip}>Attributed Actions</TableCellText>
                        </TableCell>
                        <TableCell>% Attributed Actions</TableCell>
                    </TableRow>
                </TableHead>

                <TableBody>
                    {actionData.map((action) => (
                        <TableRow key={action.label}>
                            <TablePrimaryCell>{action.label}</TablePrimaryCell>
                            <TableCell>
                                <Column gap={0} alignItems="flex-end">
                                    <HatChangeMetricWithSkeleton
                                        value={action.current.count}
                                        format={formatNumber}
                                        skeleton={skeletonWidth}
                                    />

                                    <HatChangeMetricWithSkeleton
                                        value={absChangeRatio(action.current.count, action.previous.count)}
                                        format={formatPercent}
                                        changeCode={changeCode(action.current.count, action.previous.count)}
                                        skeleton={(hasPreviousInterval && skeletonWidth) || 0}
                                    />
                                </Column>
                            </TableCell>
                            <TableCell>
                                <Column gap={0} alignItems="flex-end">
                                    <HatChangeMetricWithSkeleton
                                        value={action.current.adInfluencedCount}
                                        format={formatNumber}
                                        skeleton={skeletonWidth}
                                    />

                                    <HatChangeMetricWithSkeleton
                                        value={absChangeRatio(
                                            action.current.adInfluencedCount,
                                            action.previous.adInfluencedCount
                                        )}
                                        format={formatPercent}
                                        changeCode={changeCode(
                                            action.current.adInfluencedCount,
                                            action.previous.adInfluencedCount
                                        )}
                                        skeleton={(hasPreviousInterval && skeletonWidth) || 0}
                                    />
                                </Column>
                            </TableCell>
                            <TableCell>
                                <Column gap={0} alignItems="flex-end">
                                    <HatChangeMetricWithSkeleton
                                        value={ratio(action.current.adInfluencedCount, action.current.count)}
                                        format={formatPercent}
                                        skeleton={(hasPreviousInterval && skeletonWidth) || 0}
                                    />

                                    <HatChangeMetricWithSkeleton
                                        value={subtract(
                                            ratio(action.current.adInfluencedCount, action.current.count),
                                            ratio(action.previous.adInfluencedCount, action.previous.count)
                                        )}
                                        format={formatPercent}
                                        changeCode={changeCode(
                                            ratio(action.current.adInfluencedCount, action.current.count),
                                            ratio(action.previous.adInfluencedCount, action.previous.count)
                                        )}
                                        skeleton={(hasPreviousInterval && skeletonWidth) || 0}
                                    />
                                </Column>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>

                <TableFooter>
                    <TableRow>
                        <TableCell>Total</TableCell>
                        <TableCell>
                            <Column gap={0} alignItems="flex-end">
                                <HatChangeMetricWithSkeleton
                                    value={currentData.total?.count}
                                    format={formatNumber}
                                    skeleton={skeletonWidth}
                                />

                                <HatChangeMetricWithSkeleton
                                    value={absChangeRatio(currentData.total?.count, previousData.total?.count)}
                                    format={formatPercent}
                                    changeCode={changeCode(currentData.total?.count, previousData.total?.count)}
                                    skeleton={(hasPreviousInterval && skeletonWidth) || 0}
                                />
                            </Column>
                        </TableCell>
                        <TableCell>
                            <Column gap={0} alignItems="flex-end">
                                <HatChangeMetricWithSkeleton
                                    value={currentData.total?.adInfluencedCount}
                                    format={formatNumber}
                                    skeleton={skeletonWidth}
                                />

                                <HatChangeMetricWithSkeleton
                                    value={absChangeRatio(
                                        currentData.total?.adInfluencedCount,
                                        previousData.total?.adInfluencedCount
                                    )}
                                    format={formatPercent}
                                    changeCode={changeCode(
                                        currentData.total?.adInfluencedCount,
                                        previousData.total?.adInfluencedCount
                                    )}
                                    skeleton={(hasPreviousInterval && skeletonWidth) || 0}
                                />
                            </Column>
                        </TableCell>

                        <TableCell>
                            <Column gap={0} alignItems="flex-end">
                                <HatChangeMetricWithSkeleton
                                    value={ratio(currentData.total?.adInfluencedCount, currentData.total?.count)}
                                    format={formatPercent}
                                    skeleton={(hasPreviousInterval && skeletonWidth) || 0}
                                />

                                <HatChangeMetricWithSkeleton
                                    value={subtract(
                                        ratio(currentData.total?.adInfluencedCount, currentData.total?.count),
                                        ratio(previousData.total?.adInfluencedCount, previousData.total?.count)
                                    )}
                                    format={formatPercent}
                                    changeCode={changeCode(
                                        ratio(currentData.total?.adInfluencedCount, currentData.total?.count),
                                        ratio(previousData.total?.adInfluencedCount, previousData.total?.count)
                                    )}
                                    skeleton={(hasPreviousInterval && skeletonWidth) || 0}
                                />
                            </Column>
                        </TableCell>
                    </TableRow>
                </TableFooter>
            </Table>
        </TableContainer>
    );
}
